import { Group, Stack, Text } from "@mantine/core";
import * as Sentry from "@sentry/react";
import { isAxiosError } from "axios";
import { Component, type ReactNode } from "react";

import { Button } from "@onengine/component-library";
import { Modal } from "@/components/ui/Modal/Modal";
import { routerRef } from "@/lib/routerRef";
import { clearAllStores } from "@/utils/authHelpers";
import { saveCurrentLocationForReturn } from "@/utils/postLoginReturn";

interface State {
  hasError: boolean;
  is401: boolean;
}

export class SessionExpiredBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false, is401: false };

  static getDerivedStateFromError(error: unknown): State {
    const is401 = isAxiosError(error) && error.response?.status === 401;
    return { hasError: true, is401 };
  }

  componentDidCatch(error: unknown) {
    if (isAxiosError(error) && error.response?.status === 401) {
      saveCurrentLocationForReturn();
      clearAllStores();
      routerRef.current?.navigate({ to: "/login" });
      return;
    }
    Sentry.captureException(error);
  }

  render() {
    const { hasError, is401 } = this.state;

    if (!hasError) return this.props.children;
    if (is401) return null;

    return (
      <Modal
        isOpen={true}
        onClose={() => {}}
        title="Something went wrong"
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape={false}
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            An unexpected error occurred. Please refresh the page to continue.
          </Text>
          <Group justify="flex-end">
            <Button variant="primary" size="large" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  }
}
